import React from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import {
  CHAT_SUB_ROUTES,
  E_LEARNING_SUB_ROUTES,
  MONTHLY_INSPECTION_SUB_ROUTES,
  ROUTES,
  STORE_INSPECTION_RESULTS_SUB_ROUTES,
  STORE_SUB_ROUTES,
  USER_SUB_ROUTES,
} from "./constants/routes";
import { useAuth } from "./contexts/AuthContext";
import { ECOMPANY_BUSINESS, RoleEnum } from "./types";

// Import pages
import {
  ErrorBoundary,
  Screen403,
  Screen404,
  Screen500,
} from "./components/Screens";
import CompanyDetailWrapper from "./pages/Company Details/CompanyDetailWrapper";
import ForgotPassword from "./pages/Forgot Password/ForgotPassword";
import ForgotPasswordSent from "./pages/Forgot Password/ForgotPasswordSent";
import ResetPassword from "./pages/Forgot Password/ResetPassword";
import Login from "./pages/Login/Login";
import Notifications from "./pages/Notifications/Notifications";
import ELearning from "./pages/e-learning";
import DoneQuizCategory from "./pages/e-learning/components/DoneQuizCategory";
import QuizCategory from "./pages/e-learning/components/QuizCategory";
import QuizResult from "./pages/e-learning/components/QuizResult";
import Units from "./pages/e-learning/components/Units";
import { MonthlyInspectionList } from "./pages/monthly-inspection";
import { StoreInspectionResultsList, StoreInspectionResultDetail } from "./pages/store-inspection-results";
import {
  MonthlyCheckList,
} from "./pages/monthly-check";
import StoreDetail from "./pages/storeDetail";
import UpdateUserInformation from "./pages/updateUser";
import UserLearningDetail from "./pages/userLearningDetail";
import Chat from "./pages/Chat/Chat";
import RightSection from "./pages/Chat/RightSection/RightSection";

// Import components
import AppLayout from "./components/AppLayout";
import ProtectedRoute, { StoreManagerRoute } from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
import { LoadingScreen } from "./components/LoadingScreen";

import "./App.css";

const ELearningRoutes: React.ReactElement = (
  <>
    <Route path={ROUTES.E_LEARNING} element={<ELearning />} />
    <Route path={E_LEARNING_SUB_ROUTES.UNITS} element={<Units />} />
    <Route path={E_LEARNING_SUB_ROUTES.QUIZ_RESULT} element={<QuizResult />} />
    <Route
      path={E_LEARNING_SUB_ROUTES.QUIZ_CATEGORY}
      element={<QuizCategory />}
    />
    <Route
      path={E_LEARNING_SUB_ROUTES.DONE_QUIZ_CATEGORY}
      element={<DoneQuizCategory />}
    />
  </>
);

const CompanyRoutes: React.ReactElement = (
  <Route path={ROUTES.COMPANY} element={<CompanyDetailWrapper />} />
);

const StoreRoutes: React.ReactElement = (
  <Route path={STORE_SUB_ROUTES.DETAIL} element={<StoreDetail />} />
);

const UserRoutes: React.ReactElement = (
  <>
    <Route path={USER_SUB_ROUTES.DETAIL} element={<UpdateUserInformation />} />
    <Route path={USER_SUB_ROUTES.LEARNING} element={<UserLearningDetail />} />
    <Route path={ROUTES.ME} element={<UpdateUserInformation />} />
  </>
);

const NotificationRoutes: React.ReactElement = (
  <Route path={ROUTES.NOTIFICATION_MANAGEMENT} element={<Notifications />} />
);

const ChatRoutes: React.ReactElement = (
  <Route path={ROUTES.CHAT} element={<Chat />}>
    <Route path={CHAT_SUB_ROUTES.CHAT_ROOM} element={<RightSection />} />
  </Route>
);

const MonthlyCheckRoutes: React.ReactElement = (
  <Route
    path={ROUTES.MONTHLY_CHECK}
    element={
      <ErrorBoundary>
        <MonthlyCheckList />
      </ErrorBoundary>
    }
  />
);

const MonthlyInspectionRoutes: React.ReactElement = (
  <>
    <Route
      path={ROUTES.INSPECTION}
      element={
        <ErrorBoundary>
          <MonthlyInspectionList />
        </ErrorBoundary>
      }
    />
    <Route
      path={MONTHLY_INSPECTION_SUB_ROUTES.DETAIL}
      element={
        <ErrorBoundary>
          <MonthlyInspectionList />
        </ErrorBoundary>
      }
    />
  </>
);

const StoreInspectionResultsRoutes: React.ReactElement = (
  <>
    <Route
      path={ROUTES.STORE_INSPECTION_RESULTS}
      element={
        <StoreManagerRoute>
          <StoreInspectionResultsList />
        </StoreManagerRoute>
      }
    />
    <Route
      path={STORE_INSPECTION_RESULTS_SUB_ROUTES.DETAIL}
      element={
        <StoreManagerRoute>
          <StoreInspectionResultDetail />
        </StoreManagerRoute>
      }
    />
  </>
);

function App(): React.ReactElement {
  const { role, isLoading, user, isAuthenticated } = useAuth();

  const isStoreInType4Or5Company = user?.company.business
    ? !![ECOMPANY_BUSINESS.CS_RETAIL, ECOMPANY_BUSINESS.TS_RETAIL].includes(
        user?.company.business
      )
    : false;

  const getRoutesForRole = (): React.ReactElement => {
    switch (role) {
      case RoleEnum.SUPER_ADMIN:
        return <></>;

      case RoleEnum.COMPANY_ADMIN:
        return (
          <>
            {CompanyRoutes}
            {ELearningRoutes}
            {StoreRoutes}
            {UserRoutes}
            {NotificationRoutes}
            {ChatRoutes}
            {MonthlyCheckRoutes}
          </>
        );

      case RoleEnum.HEAD_OFFICE_USER:
        return (
          <>
            {ELearningRoutes}
            {CompanyRoutes}
            {StoreRoutes}
            {UserRoutes}
            {NotificationRoutes}
            {ChatRoutes}
          </>
        );

      case RoleEnum.STORE_MANAGER:
        if (!isStoreInType4Or5Company) {
          return (
            <>
              {ELearningRoutes}
              {StoreRoutes}
              {UserRoutes}
              {NotificationRoutes}
              {ChatRoutes}
              {StoreInspectionResultsRoutes}
            </>
          );
        }
        return (
          <>
            {ELearningRoutes}
            {StoreRoutes}
            {UserRoutes}
            {NotificationRoutes}
            {ChatRoutes}
            {StoreInspectionResultsRoutes}
            {MonthlyInspectionRoutes}
          </>
        );

      case RoleEnum.USER:
        return (
          <>
            {ELearningRoutes}
            {UserRoutes}
            {NotificationRoutes}
            {ChatRoutes}
          </>
        );

      case RoleEnum.OTHER:
      default:
        return (
          <>
            {UserRoutes}
            {ChatRoutes}
          </>
        );
    }
  };

  const indexRoute = !role
    ? ROUTES.LOGIN
    : role === RoleEnum.OTHER
    ? ROUTES.CHAT
    : ROUTES.E_LEARNING;

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route index element={<Navigate to={ROUTES.LOGIN} replace />} />
        <Route path={ROUTES.LOGIN} element={<Login />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
        <Route
          path={ROUTES.FORGOT_PASSWORD_SENT}
          element={<ForgotPasswordSent />}
        />
        <Route path={ROUTES.NEW_PASSWORD} element={<ResetPassword />} />
        <Route path={ROUTES.ERROR_500} element={<Screen500 />} />
        <Route path="*" element={<Navigate to={ROUTES.LOGIN} replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route index element={<Navigate to={indexRoute} replace />} />
      <Route element={<PublicRoute />}>
        <Route path={ROUTES.LOGIN} element={<Login />} />
        <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
        <Route
          path={ROUTES.FORGOT_PASSWORD_SENT}
          element={<ForgotPasswordSent />}
        />
        <Route path={ROUTES.NEW_PASSWORD} element={<ResetPassword />} />
      </Route>

      {/* Error pages */}
      <Route path={ROUTES.ERROR_404} element={<Screen404 />} />
      <Route path={ROUTES.ERROR_500} element={<Screen500 />} />
      <Route path={ROUTES.ERROR_403} element={<Screen403 />} />

      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        {getRoutesForRole()}
      </Route>

      {/* Catch all route */}
      <Route path="*" element={<Screen404 />} />
    </Routes>
  );
}

export default App;
